import store from '~/renderer/store';
import { defaultPrintSettings } from '~/renderer/store/hub/default';
import { TEST_IMAGE } from '~/renderer/utils';
import { sanitizeLogo } from '~/renderer/utils/config';
import {
  ditherImage,
  extractBarcodeParams,
  generateBarcode,
} from '~/renderer/utils/transform-html/utils';

import type { RECEIPT_PRINTER_BRANDS } from '~/main/printer/types';

interface TransformedImage {
  input: string;
  output: string;
}

// Avoid repeating dithering images on subsequent prints
let cachedDitheredImages: { [key: string]: string } = {};

export const clearDitheredImagesCache = () => {
  cachedDitheredImages = {};
};

async function transformImage(
  image: string,
  brand: RECEIPT_PRINTER_BRANDS,
): Promise<TransformedImage> {
  const parser = new DOMParser();
  const img = parser.parseFromString(image, 'text/html').body.firstChild as HTMLImageElement;
  // @NOTE replace this strict barcode image check for multi-product
  const isBarcode = /\/barcode\.php/.test(img.src);

  // Store copy is missing a logo width, 225 is the standard
  if (!img.width) {
    img.width = 225;
  }

  let src = '';
  let isValid = true;
  const cachedImage = cachedDitheredImages[img.src];
  if (isBarcode) {
    // eslint-disable-next-line no-await-in-loop
    src = await generateBarcode(extractBarcodeParams(img.src));
  } else if (cachedImage) {
    src = cachedImage;
  } else {
    const { printSettings } = store.getState().hub;

    let imageSrc;
    if (img.src.startsWith('data:image/')) {
      imageSrc = Buffer.from(img.src.replace(/^data:image\/.+?;base64,/, ''), 'base64');
    } else {
      imageSrc = sanitizeLogo(img.src);
    }

    const ditheredImage = await ditherImage({
      image: imageSrc,
      brand,
      printSettings: img.src.endsWith(TEST_IMAGE) ? defaultPrintSettings : printSettings,
    });

    if (ditheredImage) {
      src = `data:image/png;base64, ${ditheredImage}`;
    } else {
      isValid = false;
    }

    if (src) {
      cachedDitheredImages[img.src] = src;
    }
  }

  img.src = src;

  return {
    input: image,
    output: isValid ? img.outerHTML : '',
  };
}

export const transformHtmlImages = async (
  html: string,
  brand: RECEIPT_PRINTER_BRANDS,
): Promise<string> => {
  let result = html;
  const promises: Promise<TransformedImage>[] = [];

  const regex = /<img[\s\S]+?\/?>/g;
  let match = regex.exec(html);
  while (match && match[0]) {
    promises.push(transformImage(match[0], brand));
    match = regex.exec(html);
  }

  const images = await Promise.all(promises);
  for (const image of images) {
    result = result.replace(image.input, image.output);
  }

  return result;
};
